import { AccountLicense } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import React from 'react';
import { useIntl } from 'react-intl';

import useCheckLicense from '../../common/hooks/useCheckLicense';
import { UiUpgradePopOver } from '../common/UiUpgradeChip/UiUpgradePopOver';

interface TenantUpgradePopoverProps {
    anchorEl: HTMLElement | null;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
}

export const TenantUpgradePopover: React.FC<TenantUpgradePopoverProps> = ({
    anchorEl,
    setAnchorEl,
}) => {
    const { formatMessage: translate } = useIntl();
    const { includesLicense } = useCheckLicense();
    const EnablePricingV2 = useFeatureFlagValue(Features.EnablePricingV2.name);

    // Determine tenant upgrade license type
    let tenantUpgradeLicenseType: AccountLicense.ENTERPRISE | AccountLicense.PRO | -1 | undefined;
    if (EnablePricingV2) {
        if (includesLicense([ AccountLicense['PRO-TRIAL'] ])) {
            // Upgrade to Standard Trial flow
            tenantUpgradeLicenseType = AccountLicense.PRO;
        } else if (includesLicense([ AccountLicense.FREEKIT, AccountLicense.PRO, AccountLicense.COMMUNITY, AccountLicense['STD-TRIAL'] ])) {
            // Contact sales flow
            tenantUpgradeLicenseType = AccountLicense.ENTERPRISE;
        } else {
            tenantUpgradeLicenseType = undefined;
        }
    } else {
        if (includesLicense([ AccountLicense.FREEKIT, AccountLicense.COMMUNITY ])) {
            tenantUpgradeLicenseType = AccountLicense.PRO;
        } else if (includesLicense([ AccountLicense.PRO, AccountLicense['PRO-TRIAL'] ])) {
            tenantUpgradeLicenseType = AccountLicense.ENTERPRISE;
        } else {
            tenantUpgradeLicenseType = undefined;
        }
    }

    if (tenantUpgradeLicenseType == null) {
        return null;
    }

    const upgradePathLicenseKey = AccountLicense[tenantUpgradeLicenseType];

    let iconDescription;
    if (EnablePricingV2) {
        // Do not display icon description for Basic Trial license
        iconDescription = includesLicense([ AccountLicense['PRO-TRIAL'] ])
            ? undefined
            : translate({ id: 'CLIENT_CHIP_MAX_TENANTS_CUSTOMIZATION_ICON_DESCRIPTION_ENTERPRISE' });
    } else {
        iconDescription = translate({ id: `CLIENT_CHIP_MAX_TENANTS_CUSTOMIZATION_ICON_DESCRIPTION_${upgradePathLicenseKey}`.toUpperCase() });
    }

    const description = EnablePricingV2
        ? translate({ id: 'CLIENT_CHIP_MAX_TENANTS_CUSTOMIZATION_DESCRIPTION_V2' })
        : translate({ id: `CLIENT_CHIP_MAX_TENANTS_CUSTOMIZATION_DESCRIPTION_${AccountLicense[tenantUpgradeLicenseType]}`.toUpperCase() });

    return (
        <UiUpgradePopOver
            title={translate({ id: 'CLIENT_CHIP_MAX_TENANTS_CUSTOMIZATION_HEADER_NEED_MORE_TENANTS' })}
            description={description}
            iconDescription={iconDescription}
            licenseType={tenantUpgradeLicenseType}
            icon={<FormatListNumberedOutlinedIcon />}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            telemetryTitle="AddTenant"
        />
    );
};
